import { FC, Fragment, JSX, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/joy';
import {
  Input,
  CountrySelector,
  Dropdown,
} from '@consolidocs-ui/components';
import {
  COMPANY_FORM_FIELDS,
  COUNTRIES,
  COMPANY_TYPE,
  useToast,
} from '@consolidocs-ui/utils';
import { CompanyType } from '@consolidocs-ui/types';
import { IndustryType } from '@consolidocs-ui/types';
import { ContractType } from '@consolidocs-ui/types';
import { useGetIndustriesQuery } from '@consolidocs-ui/data-access/IndustryApi';

type Errors = Partial<Record<keyof CompanyType, string>>;

interface Step1Props {
  formData: CompanyType;
  errors: Errors;
  editData?: CompanyType;
  onChange: (field: keyof CompanyType, value: unknown) => void;
}

/**
 * Step 1 — organization / company details.
 * Renders the exact same fields and layout as the previous single-step form so
 * that the UX for existing users is unchanged.
 */
const Step1CompanyDetails: FC<Step1Props> = ({
  formData,
  errors,
  editData,
  onChange,
}): JSX.Element => {
  const { data: industries } = useGetIndustriesQuery();
  const [triggerToast] = useToast();

  const industryOptions =
    industries?.data?.map((ind: IndustryType) => ({
      label: ind.name,
      value: ind.id,
    })) ?? [];

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.label === formData.country) || null,
    [formData.country]
  );

  const [industryContractOptions, setIndustryContractOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (!formData.industry) {
      setIndustryContractOptions([]);
      if ((formData.contract_types?.length ?? 0) > 0) {
        onChange('contract_types', []);
      }
      return;
    }

    const selectedIndustry = industries?.data?.find(
      (ind: IndustryType) => ind.id === formData.industry
    );

    if (selectedIndustry?.contract_types?.length) {
      const options = selectedIndustry.contract_types.map(
        (ct: ContractType) => ({ label: ct.name, value: ct.id })
      );
      setIndustryContractOptions(options);

      if (editData && editData.contract_types_display?.length) {
        const matched = editData.contract_types_display
          .map((ct) => ct.id)
          .filter((id: string) => options.some((opt) => opt.value === id));
        onChange('contract_types', matched);
      }
    } else {
      setIndustryContractOptions([]);
      if ((formData.contract_types?.length ?? 0) > 0) {
        onChange('contract_types', []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.industry, industries, editData]);

  return (
    <Box className="wizard-step grid grid-cols-1 md:grid-cols-2 gap-4">
      {COMPANY_FORM_FIELDS.map(({ label, field, required }) => {
        // Admin email is only set on creation.
        if (field === 'email' && editData) {
          return <Fragment key={field}></Fragment>;
        }

        return (
          <Input
            label={label}
            required={required}
            value={(formData[field as keyof CompanyType] as string) || ''}
            autoComplete="off"
            onChange={(e) =>
              onChange(field as keyof CompanyType, e.target.value)
            }
            key={field}
            name={field}
            error={!!errors[field as keyof CompanyType]}
            errorMessage={errors[field as keyof CompanyType]}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
      })}

      <div>
        <CountrySelector
          required
          value={selectedCountry}
          onChange={(value) => onChange('country', value?.label || '')}
        />
      </div>

      <Dropdown
        options={COMPANY_TYPE}
        label="Company Type"
        required
        error={errors.companyType}
        value={formData.companyType}
        size="sm"
        // Company type cannot be changed after creation.
        disabled={!!editData}
        onChange={(_, value) => onChange('companyType', value || '')}
      />

      <Dropdown
        label="Industry"
        required
        options={industryOptions}
        value={formData.industry}
        onChange={(_, value) => onChange('industry', value || '')}
        error={errors.industry}
      />

      <Dropdown
        label="Contract Types"
        required
        options={industryContractOptions}
        multiple
        disabled={!formData.industry}
        value={formData.contract_types || []}
        onClick={() => {
          if (!formData.industry) {
            triggerToast({
              message: 'Please select an Industry first.',
              type: 'danger',
            });
          }
        }}
        onChange={(_, selectedValues) => {
          onChange('contract_types', selectedValues || []);
        }}
        error={
          errors.contract_types ||
          (formData.industry && industryContractOptions.length === 0
            ? 'No contract types defined for this industry. Please add them first.'
            : '')
        }
      />
    </Box>
  );
};

export default Step1CompanyDetails;
